// MatchPairGrid — side-by-side match evidence for a reconcile row that HAS a
// suggested match (the Confident + Needs-eyes piles). Each fact sits in a paired
// cell with the statement value on top and the receipt value below, so the eye
// compares them in place instead of darting from the left of the row to a summary
// on the right. A per-cell ring grades the agreement (green = exact, amber = near,
// neutral = no signal); clicking anywhere on the cell cluster peeks the receipt.

import React from 'react';
import { formatEventDate } from '../utils/date-utils';
import type { ReconTxn } from './types';
import { parseBreakdown } from './EvidenceChips';
import { Tag } from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';

// Local mirrors of ReconcilePage's money formatters (two-liners; kept here so the
// cell block is self-contained). txnMoney keeps the statement's signed convention,
// but confident/review charges are always purchases, so this reads as a plain "$X".
const money = (v: unknown) => (v == null || v === '' ? '—' : `$${Math.abs(Number(v)).toFixed(2)}`);
const txnMoney = (v: unknown) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return n > 0 ? `+$${n.toFixed(2)}` : `$${Math.abs(n).toFixed(2)}`;
};

type Verdict = 'green' | 'amber' | 'neutral';
const RING: Record<Verdict, string> = {
  green: 'ring-1 ring-inset ring-green-500/40 bg-green-500/5',
  amber: 'ring-1 ring-inset ring-amber-500/40 bg-amber-500/5',
  neutral: 'ring-1 ring-inset ring-outline-variant',
};

// One paired fact: statement on top, receipt below. `bottomTrail` rides beside the
// receipt line (the "unreviewed" pill); `extra` drops below the pair (the tip note).
function PairCell({
  verdict, top, bottom, bottomTrail, extra, align, mono, topLabel, bottomLabel, className = '',
}: {
  verdict: Verdict;
  top: string;
  bottom: string;
  bottomTrail?: React.ReactNode;
  extra?: React.ReactNode;
  align?: 'right';
  mono?: boolean;
  topLabel?: string;   // quiet "stmt"/"rcpt" cue — first cell only, teaches the pattern
  bottomLabel?: string;
  className?: string;
}) {
  const monoCls = mono ? 'font-mono tabular-nums' : '';
  const justify = align === 'right' ? 'justify-end' : '';
  return (
    <div className={`flex flex-col justify-center rounded-md px-2.5 py-1.5 min-w-0 ${RING[verdict]} ${className}`}>
      <div className={`flex items-baseline gap-1 min-w-0 ${justify}`}>
        {topLabel && <span className="shrink-0 text-[9px] uppercase tracking-wide text-on-surface-variant">{topLabel}</span>}
        <span className={`min-w-0 truncate text-sm text-on-surface ${monoCls}`}>{top}</span>
      </div>
      <div className={`mt-0.5 flex items-baseline gap-1 min-w-0 ${justify}`}>
        {bottomLabel && <span className="shrink-0 text-[9px] uppercase tracking-wide text-on-surface-variant">{bottomLabel}</span>}
        <span className={`min-w-0 truncate text-xs text-on-surface-variant group-hover:underline ${monoCls}`}>{bottom}</span>
        {bottomTrail}
      </div>
      {extra}
    </div>
  );
}

export default function MatchPairGrid({
  txn, onPeek, action, categoryControl,
}: {
  txn: ReconTxn;
  onPeek: (pid: string) => void;
  action: React.ReactNode;
  // Dawn's inline expense-category control (financial only). Rides the row's right
  // cluster, OUTSIDE the peek button, so it never nests interactive controls in a
  // <button> nor competes with the row's keyboard focus.
  categoryControl?: React.ReactNode;
}) {
  const m = txn.match;
  if (!m) return null;
  const b = parseBreakdown(m.score_breakdown);

  // ── per-cell verdicts (drive the ring color) ──────────────────────────────
  // All keyed off the engine's breakdown; with no breakdown (a pre-rematch
  // suggestion) every cell stays neutral and just shows the raw values.
  const delta = b?.dateDeltaDays;
  const dateVerdict: Verdict =
    !m.receipt_date || delta == null ? 'neutral' : delta === 0 ? 'green' : 'amber';

  const tip = b?.impliedTip;
  const amountVerdict: Verdict =
    b?.amountExact ? 'green' : tip != null && tip > 0 ? 'amber' : 'neutral';

  const vs = b?.vendorScore;
  const vendorVerdict: Verdict =
    vs == null || vs < 0.2 ? 'neutral' : vs >= 0.5 ? 'green' : 'amber';

  // Card is exception-only: silent when it agrees, a red note when the read card
  // disagrees with the charge's card (the fuzzy tier can suggest across cards).
  const cardMismatch = b?.last4 === false && !!m.receipt_card_last4;
  const unreviewed = !!b?.receiptUnreviewed;
  const aiReviewPerfect = !!m.receipt_ai_review_perfect;

  // One quiet sentence under the row when the match isn't cleanly 1:1.
  const ties = b?.chargeAmbiguous
    ? 'Another receipt fits this charge equally well, verify before confirming.'
    : b?.receiptContested
      ? 'This receipt also fits another charge equally well.'
      : null;

  return (
    <>
      <div className="flex flex-wrap items-stretch gap-x-4 gap-y-2">
        <Tooltip label="Peek at this receipt">
        <button type="button" onClick={() => onPeek(m.receipt_public_id)}
          className="group flex flex-1 flex-wrap items-stretch gap-2 min-w-0 text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
          {/* Date: statement over receipt (the one cell that carries the microlabels) */}
          <PairCell className="w-32 shrink-0" verdict={dateVerdict} topLabel="stmt" bottomLabel="rcpt"
            top={formatEventDate(txn.txn_date)}
            bottom={m.receipt_date ? formatEventDate(m.receipt_date) : '—'} />
          {/* Amount: charge over receipt total, with an implied-tip footnote when they gap */}
          <PairCell className="w-28 shrink-0" verdict={amountVerdict} align="right" mono
            top={txnMoney(txn.amount)}
            bottom={money(m.receipt_total)}
            extra={(
              <Reveal show={tip != null && tip > 0}>
                <div className="pt-0.5 text-right text-[10px] text-amber-300/80">{`+$${(tip ?? 0).toFixed(2)} tip?`}</div>
              </Reveal>
            )} />
          {/* Vendor: statement description over receipt vendor; takes the leftover width */}
          <div className="flex-1 min-w-[10rem] flex flex-col">
            <PairCell className="flex-1" verdict={vendorVerdict}
              top={txn.description || '—'}
              bottom={m.vendor_name || '—'}
              bottomTrail={(
                <>
                  <Appear show={aiReviewPerfect} className="shrink-0">
                    <Tag size="sm" tone="good" className="shrink-0" title="The reader filed this cleanly; no buyer review was needed. Confirm it against the statement as usual.">AI Review Perfect</Tag>
                  </Appear>
                  <Appear show={!aiReviewPerfect && unreviewed} className="shrink-0">
                    <Tag size="sm" tone="warn" className="shrink-0">unreviewed</Tag>
                  </Appear>
                </>
              )} />
            <Reveal show={cardMismatch}>
              <div className="pt-1">
                <Tag size="sm" tone="bad" className="self-start">
                  card mismatch ····{m.receipt_card_last4}
                </Tag>
              </div>
            </Reveal>
          </div>
        </button>
        </Tooltip>
        <div className="flex items-center gap-3 shrink-0">
          {categoryControl}
          {action}
        </div>
      </div>
      <Reveal show={!!ties}>
        <p className="pt-1.5 text-xs text-on-surface-variant leading-snug">{ties}</p>
      </Reveal>
    </>
  );
}
